import { Avatar, Tooltip, notification, Badge, Button, Modal } from "antd";
import React from "react";
import { useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { siteInfo } from "../utils/SiteInfo";
import {
  CloudServerOutlined,
  GoldOutlined,
  HomeOutlined,
  TeamOutlined,
  UsergroupAddOutlined,
} from "@ant-design/icons";
import { getData } from "../api/request";
import {
  IoCashOutline,
  IoChatbox,
  IoChatboxOutline,
  IoChatbubblesOutline,
  IoCloseCircle,
} from "react-icons/io5";

export default function Navbar(props) {
  let profile = useSelector((state) => state.auth?.profile);

  let accessCrm = localStorage.getItem("accessCrm")
    ? JSON.parse(localStorage.getItem("accessCrm"))
    : null;

  const location = useLocation();
  const navigate = useNavigate();

  let routes = [
    {
      path: "/",
      title: "Dashboard",
      icon: HomeOutlined,
    },
    {
      path: "/purchasing",
      title: "Purchasing",
      icon: CloudServerOutlined,
    },
    {
      path: "/orders",
      title: "Orders",
      icon: GoldOutlined,
    },
    {
      path: "/customers",
      title: "Customers",
      icon: TeamOutlined,
    },
    {
      path: "/payments",
      title: "Payments",
      icon: IoCashOutline,
    },
    {
      path: "/admins",
      title: "Admins",
      icon: UsergroupAddOutlined,
    },
  ];

  const auth = useSelector((state) => state.auth);

  const [chatRooms, setChatRooms] = React.useState(null);
  const [isModalVisible, setIsModalVisible] = React.useState(false);

  const my_id = useSelector((state) => state.auth?.profile?._id);

  const isInitialMountRef = React.useRef(true);
  const pathnameRef = React.useRef(location.pathname);

  // Sync pathname to ref and handle state cleanup when entering chat page
  React.useEffect(() => {
    pathnameRef.current = location.pathname;
    if (location.pathname === "/customer-chatbox") {
      setChatRooms(null);
    }
  }, [location.pathname]);

  React.useEffect(() => {
    if (!auth?.isAuthenticated) return;

    const fetchChats = () => {
      getData(
        "admin/customer-chats",
        (data) => {
          if (data && data.result) {
            const newDocs = data.result.docs || [];

            const getUnrepliedCount = (docs) => {
              if (!docs) return 0;
              return docs.filter((room) => room.chat?.admin_replied === false)
                .length;
            };

            const newUnrepliedCount = getUnrepliedCount(newDocs);

            setChatRooms((prevChatRooms) => {
              if (prevChatRooms !== null) {
                const oldUnrepliedCount = getUnrepliedCount(prevChatRooms);
                if (newUnrepliedCount > oldUnrepliedCount) {
                  setIsModalVisible(true);
                }
              }
              return newDocs;
            });
          }
        },
        { page: 1, per_page: 10, manager: my_id },
        true,
      );
    };

    // Initial call on mount if document is visible and not in chatbox
    if (
      !document.hidden &&
      pathnameRef.current !== "/customer-chatbox" &&
      isInitialMountRef.current
    ) {
      fetchChats();
      isInitialMountRef.current = false;
    }

    const intervalId = setInterval(() => {
      if (!document.hidden && pathnameRef.current !== "/customer-chatbox") {
        fetchChats();
      }
    }, 60000);

    return () => {
      clearInterval(intervalId);
    };
  }, [auth?.isAuthenticated, my_id]);

  const hasUnreplied = React.useMemo(() => {
    if (!chatRooms) return false;
    return chatRooms.some((room) => room.chat?.admin_replied === false);
  }, [chatRooms]);

  const getRouteName = (pathname) => {
    let found = routes.find((el) => el.path === pathname);
    if (found) {
      return (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <found.icon size={18} color="#fff" />
          <span
            style={{
              color: "#fff",
              marginLeft: 8,
              textTransform: "capitalize",
            }}
          >
            {found.title}
          </span>
        </div>
      );
    } else {
      let arr = pathname.split("/");
      let name = arr[arr.length - 1];

      console.log(name);

      name =
        name && typeof name === "string" ? name?.split("-").join(" ") : name;

      if (pathname.includes("/create-post")) {
        name = "Create Post";
      }
      if (pathname.includes("/edit-post")) {
        name = "Edit Post";
      }
      if (pathname.includes("/delivery/")) {
        name = "Invoice";
      }
      if (pathname.includes("/cash-counter/")) {
        name = "Cash Counter";
      }
      if (pathname.includes("/campaign/")) {
        name = "Campaign Details";
      }
      if (pathname.includes("agent/orders")) {
        name = "Agent Orders";
      }
      if (pathname.includes("/agent/withdraw/")) {
        name = "Agent Withdraw";
      }

      if (pathname.includes("/skyexpress-bookings")) {
        name = "SkyExpress Bookings";
      }
      if (pathname.includes("/crm/customer")) {
        name = "Customer Details";
      }

      if (pathname === "/purchasing") {
        name = "Air Purchase";
      }

      if (pathname.includes("/shipping-categories")) {
        name = "Shipping Categories";
      }

      if (pathname.includes("/my-crm")) {
        name = "My CRM";
        if (pathname.length > 10) {
          name = "Manager CRM";
        }
      }
      if (pathname.includes("/bulk-order-carton-list")) {
        name = "Bulk Order Carton List";
      }
      if (pathname.includes("/gallery/")) {
        name = "Image Gallery";
      }

      return (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <span style={{ color: "#fff", textTransform: "capitalize" }}>
            {name}
          </span>
        </div>
      );
    }
  };

  return (
    <>
      {location.pathname !== "/login" && (
        <nav
          style={{
            background: `linear-gradient(90deg, ${siteInfo.navColor} 50%, #056b3b)`,
            padding: "0px 1.5rem",
            position: "fixed",
            boxShadow: "0 1px 16px rgba(0,0,0,.1)",
            borderBottom: "1px solid rgba(0,0,0,0.08)",
            zIndex: 1,
            height: "60px",
            color: "white",
            top: 0,
            left: 0,
            right: 0,
            display: auth?.isAuthenticated ? "block" : "none",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              height: "100%",
              justifyContent: "space-between",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <a className="flex align-center justify-center" href="/">
                <img
                  style={{ width: "100px" }}
                  src={require("../assets/logo2.png")}
                  alt=""
                />
              </a>
              {/* {getRouteName(location.pathname)} */}
            </div>
            <div style={{ display: "flex", alignItems: "center" }}>
              {
                <Link
                  to="/customer-chatbox"
                  style={{
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <style>{`
                    @keyframes chat-pulse {
                      0% {
                        transform: scale(1);
                        box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.7);
                      }
                      50% {
                        transform: scale(1.12);
                        box-shadow: 0 0 0 8px rgba(255, 255, 255, 0);
                      }
                      100% {
                        transform: scale(1);
                        box-shadow: 0 0 0 0 rgba(255, 255, 255, 0);
                      }
                    }
                    .chat-icon-animated {
                      animation: chat-pulse 1.8s infinite ease-in-out;
                    }
                  `}</style>
                  <Badge dot={hasUnreplied} color="#ff4d4f">
                    <Avatar
                      className={hasUnreplied ? "chat-icon-animated" : ""}
                      style={{
                        backgroundColor: "#ffffffff",
                        color: siteInfo.navColor,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                      icon={<IoChatbubblesOutline size={22} />}
                    />
                  </Badge>
                </Link>
              }

              {accessCrm ? (
                <div
                  className="flex align-center"
                  style={{
                    gap: 8,
                    background: "#E4004B",
                    borderRadius: 12,
                    padding: "4px 8px",
                    cursor: "pointer",
                    marginLeft: 12,
                  }}
                  onClick={() => {
                    localStorage.removeItem("accessCrm");
                    window.location.href = "/";
                  }}
                >
                  <Avatar
                    style={{ backgroundColor: "#fde3cf", color: "#f56a00" }}
                  >
                    {accessCrm?.crm?.manager_type}
                  </Avatar>
                  <div>
                    <p>Accessing CRM</p>
                    <p
                      style={{
                        fontSize: 12,
                      }}
                    >
                      {accessCrm?.name}
                    </p>
                  </div>
                  <IoCloseCircle size={24} color="#fff" />
                </div>
              ) : (
                <Link to="/profile" className="logout">
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                    }}
                  >
                    <Avatar
                      style={{ backgroundColor: "#fde3cf", color: "#f56a00" }}
                    >
                      U
                    </Avatar>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        marginLeft: "0.5rem",
                      }}
                    >
                      <span
                        style={{
                          lineHeight: "18px",
                          fontWeight: "bold",
                          color: "#f0f0f0",
                        }}
                      >
                        {profile?.name ? profile?.name : "Administration"}
                      </span>
                      <span
                        style={{
                          lineHeight: "18px",
                          fontWeight: "bold",
                          color: "#eee",
                          fontSize: 12,
                          textTransform: "capitalize",
                        }}
                      >
                        {profile?.phone ? profile?.phone : "role"}
                      </span>
                    </div>
                  </div>
                </Link>
              )}
            </div>
          </div>
        </nav>
      )}

      {/* Prominent Square Chat Message Notification Modal */}
      <Modal
        title={null}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        width={500}
        styles={{
          content: {
            borderRadius: "8px",
            height: "360px",
            padding: "40px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
            border: "2px solid #E4004B",
          },
          body: {
            height: '100%',
            display: 'flex'
          }
        }}
        centered
      >
        {/* Custom Header */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            marginTop: "20px",
            gap: 16,
            flex: 1
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              flex: 1
            }}
          >
            <div
              style={{
                background: "#fff2f0",
                border: "1px solid #ffccc7",
                borderRadius: "50%",
                padding: "16px",
                display: "inline-flex",
                marginBottom: "24px",
                animation: "pulseGlow 2s infinite",
              }}
            >
              <style>{`
              @keyframes pulseGlow {
                0% { box-shadow: 0 0 0 0 rgba(228, 0, 75, 0.4); }
                70% { box-shadow: 0 0 0 15px rgba(228, 0, 75, 0); }
                100% { box-shadow: 0 0 0 0 rgba(228, 0, 75, 0); }
              }
            `}</style>
              <IoChatbubblesOutline size={48} color="#E4004B" />
            </div>
            <h2
              style={{
                fontSize: "28px",
                fontWeight: "800",
                color: "#E4004B",
                margin: 0,
                textTransform: "uppercase",
                letterSpacing: "1px",
              }}
            >
              You Have a New Message
            </h2>
          </div>

          {/* <img
            src={randomImage || OFFICE_IMAGES[0]}
            alt=""
            style={{
              width: 160,
              height: 160,
              borderRadius: "50%",
              objectFit: "cover",
            }}
          /> */}

          {/* Custom Footer */}
          <div style={{ display: "flex", gap: "16px", marginTop: 12 }}>
            <Button
              type="primary"
              style={{
                flex: 1,
                height: "50px",
                fontSize: "16px",
                fontWeight: "600",
                background: "#E4004B",
                borderColor: "#E4004B",
                borderRadius: "0px",
                minWidth: 300,
              }}
              onClick={() => {
                setIsModalVisible(false);
                window.open("/customer-chatbox", "_blank");
              }}
            >
              Reply Now
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
